import { BACKPACK_SIDEBAR_NAV_ITEMS, BACKPACK_SIDEBAR_NAV_ITEMS_SETTINGS, NavItem } from '../helpers/topics-backpack';
import { BillingOption, ChargebeeCheckoutItem, FeatureTierLevel, FeatureType, GetCreatorProfileResponse, GetFolderResponse, GetPublicOrganizationResponse, GetTopicResponse, TierItemIdMap } from '../types';
import { LitElement, css, html } from 'lit';
import { createFolder, getFolder, getFolders } from '../helpers/api/folders-api';
import { customElement, property, query, state } from 'lit/decorators';
import { getFromLocalStorage, isDefined, show_confetti, undefinedIfEmpty } from '../helpers/utils';

import { Dialog } from '@material/mwc-dialog';
import { HelpMenu } from './nav-bar/help-menu';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TeamSwitchMenu } from './team-switch-menu';
import accountInfo from '../helpers/state/account-info';
import { checkoutSubscription } from '../helpers/chargebee';
import { detectMobileViewport } from '../helpers/device';
import featuresInfo from '../helpers/state/features-info';
import { getCreatorProfiles } from '../helpers/api/creator-profile-api';
import { getInitials } from '../helpers/strings';
import { getMainPage } from '../helpers/page-name';
import { getVisibleTextColor } from '../helpers/colors';
import { ifDefined } from 'lit/directives/if-defined';
import organizationInfo from '../helpers/state/organization-info';
import { palette } from '../style/variables';
import subscriptionInfo from '../helpers/state/subscription-info';

@customElement('backpack-sidebar')
export class BackpackSidebar extends LitElement {
    amplitude_callsite: string = 'Backpack Sidebar';

    @query('#addCreatorProfileModal')
    addCreatorProfileModalComponent!: Dialog;

    @query('#addFolderModalComponent')
    addFolderModalComponent?: Dialog;

    @query('#helpmenu')
    helpComponent?: HelpMenu;

    @property({ type: Boolean })
    showFolders: boolean = true;

    @property({ type: Boolean })
    showCreatorProfiles: boolean = true;

    @property({ type: String })
    selectedFolderId?: string;

    @property({ type: String })
    selectedCreatorProfileId?: string;

    @property({ type: Boolean })
    isModal: boolean = false;

    @property({ type: String })
    subscription_level_text: string = '';

    @query('#teamAccessComponent')
    teamAccessComponent!: Dialog;

    @query('#teamSwitchMenu')
    teamSwitchMenu?: TeamSwitchMenu;

    @state()
    folders: GetFolderResponse[] = [];

    @state()
    filterType?: string | undefined;

    @state()
    pageName?: string | undefined;

    @state()
    topics: GetTopicResponse[] = [];

    @state()
    organizations: GetPublicOrganizationResponse[] = [];

    @state()
    creatorProfiles: GetCreatorProfileResponse[] = [];

    @state()
    sidebarNavItems: NavItem[] = BACKPACK_SIDEBAR_NAV_ITEMS;

    @state()
    isLoading: boolean = false;

    @state()
    showPersonalTopics: boolean = false;

    @state()
    mobileMinified: boolean = detectMobileViewport();

    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindOrganizationState = new StateController(this, organizationInfo);

    async firstUpdated() {
        await this.startup();
    }

    async openWorkspaceDropdown() {
        this.teamSwitchMenu?.toggleMenuOpen();
    }

    private async startup() {
        if (organizationInfo.allOrganizations.length === 0) {
            await organizationInfo.getAllOrganizations();
        }
        this.organizations = organizationInfo.allOrganizations;
        await this.loadCreatorProfiles();
        await this.loadFolders();
    }

    private async loadCreatorProfiles() {
        if (this.showCreatorProfiles) {
            this.creatorProfiles = await getCreatorProfiles(this.amplitude_callsite);
            if (this.creatorProfiles.length > 0) {
                this.expandSidebarNavItem('Public Profiles');
            }
        }
    }

    private async loadFolders() {
        if (this.showFolders) {
            this.folders = await getFolders(this.amplitude_callsite);
            if (this.folders.length > 0) {
                this.expandSidebarNavItem('Folders');
            }
        }
    }

    private expandSidebarNavItem(label: string) {
        this.sidebarNavItems = this.sidebarNavItems.map((item) => {
            if (item.label === label) {
                item.isExpanded = true;
            }
            return item;
        });
    }

    private async triggerAddFolder() {
        if (isDefined(this.addFolderModalComponent)) {
            this.addFolderModalComponent.open = true;
        }
    }

    private async onAddCreatorProfile(e: any) {
        this.creatorProfiles = [...this.creatorProfiles, e.detail.creatorProfile];
    }

    private async selectFolderFromSidebar(e: any) {
        const folderId = e.detail.folderId as string;
        this.selectedFolderId = folderId;
        this.pageName = 'Folder';

        // add folder_id to the URL
        this.removeParamentersFromURL();
        // update url to #home?folder_id=folderId
        const url = new URL(window.location.href);
        url.hash = `home?folder_id=${folderId}`;

        window.history.pushState({}, '', url.toString());

        this.dispatchEvent(new CustomEvent('update-page-name', { detail: { pageName: this.pageName } }));
    }

    private onSelectCreatorProfile(creatorProfileId: string) {
        this.selectedCreatorProfileId = creatorProfileId;
        this.pageName = 'Public Profile';

        this.dispatchEvent(new CustomEvent('on-select-creator-profile', { detail: { creatorProfileId } }));
        this.dispatchEvent(new CustomEvent('update-page-name', { detail: { pageName: this.pageName } }));
    }

    private onUpdateFolders(e: CustomEvent) {
        this.folders = e.detail.folders;
    }

    private async checkout_feature(feature: FeatureType, billing_option: BillingOption) {
        const tier = TierItemIdMap[FeatureTierLevel.ProBase].Annual;
        if (!tier) {
            getMainPage().errorToastElement.showToastAndThrow('An Error Occured - Unable to checkout. Reach out to help.proxi.co');
            return;
        }
        const purchase_items: ChargebeeCheckoutItem[] = [
            {
                item_price_id: tier,
                quantity: subscriptionInfo.number_of_seats ?? organizationInfo.chargeableMembers.length ?? 1,
            },
        ];
        await checkoutSubscription(purchase_items, (error, result) => {
            if (error) {
                console.error(error);
            } else if (result === 'success') {
                void show_confetti();
            } else if (result === 'closed') {
                // do nothing
            }
        });
    }

    onExpandSubItems(e: Event, item: any) {
        e.stopPropagation(); // Prevent the click event from bubbling up to the parent div
        item.isExpanded = item.isExpanded === false; // Toggle the isExpanded property of the item
        this.requestUpdate(); // Request an update to re-render the component with the new state
    }

    private async addFolder(e: CustomEvent) {
        const folderRequest = {
            name: e.detail.name,
            organization_id: undefined,
            authorizations: undefined,
            parent_folder_id: e.detail.folderId,
        };

        const addResult = await createFolder(
            this.amplitude_callsite,
            folderRequest,
            {}
        );

        const folderData = await getFolder(this.amplitude_callsite, addResult._id.$oid);

        this.folders = [...this.folders, folderData];
        this.addFolderModalComponent?.close();
    }

    private removeParamentersFromURL() {
        window.location.assign(`#home`);
        this.dispatchEvent(new CustomEvent('on-select-backpack-filter-type', {
            detail: {
                filterTypeInfo: undefined,
            }
        }));
    }

    private onSelectNavItem(filterTypeInfo: NavItem) {
        if (filterTypeInfo.clickable === false) {
            return;
        }
        this.pageName = filterTypeInfo.label;

        if (this.pageName === 'Backpack' || (filterTypeInfo.url?.includes('filter_type') === true )) {
            this.filterType = filterTypeInfo.filterType;
            this.dispatchEvent(new CustomEvent('update-page-name', { detail: { pageName: 'Backpack' } }));
            this.dispatchEvent(new CustomEvent('on-select-backpack-filter-type', { detail: { filterTypeInfo } }));
        } else {
            this.removeParamentersFromURL();
            this.dispatchEvent(new CustomEvent('update-page-name', { detail: { pageName: this.pageName } }));
        }
    }

    private onOpenAccountInfo() {
        this.pageName = 'Account Settings';
        this.removeParamentersFromURL();
        this.dispatchEvent(new CustomEvent('update-page-name', {
            detail: {
                pageName: 'Account Settings'
            }
        }));
    }


    private onOpenDiscover() {
        this.pageName = 'Discover';
        this.removeParamentersFromURL();
        this.dispatchEvent(new CustomEvent('update-page-name', {
            detail: {
                pageName: 'Discover'
            }
        }));
    }

    private teamAccessClose() {
        this.teamAccessComponent.close();
    }

    private onStartCreateTeam() {
        this.teamAccessComponent.open = true;
    }

    private async onTeamCreated(e: CustomEvent) {
        await organizationInfo.getAllOrganizations();
        this.teamAccessClose();
        if (isDefined(e.detail.organization)) {
            await this.postOrganizationUpgrade(e.detail.organization._id.$oid);
        }
    }

    private async postOrganizationUpgrade(organization_id: string | undefined) {
        // set local storage
        if (!organization_id) {
            localStorage.removeItem('organization_id');
        } else {
            localStorage.setItem('organization_id', organization_id);
        }
        // refresh the page
        window.location.reload();
    }


    render() {
        const org_background_color = organizationInfo.organization?.primary_color ?? 'var(--main-primary-color)';
        const org_text_color = organizationInfo.organization?.secondary_color ?? getVisibleTextColor(palette.primaryBlue) ?? 'white';
        return html`
            <div class="backpack-sidebar__wrapper">
                <div class="backpack-sidebar__wrapper_main">
                    ${!this.isModal
                        ? html`
                            <div class="switchMenuContainer">
                                <team-switch-menu
                                    id="teamSwitchMenu"
                                    .organizations=${organizationInfo.allOrganizations}
                                    amplitude_callsite=${this.amplitude_callsite}
                                    @on-start-create-team=${this.onStartCreateTeam}
                                >
                                </team-switch-menu>
                            </div>

                            ${this.mobileMinified ? '' : html`
                                <div class="backpack-sidebar__backpack-links">
                                    ${this.sidebarNavItems?.map(
                                        (item) =>
                                            (item.teamOnly === true && !organizationInfo.organization) || (organizationInfo.isGuest && item.teammateOnly === true) ?
                                            '':
                                                html`
                                                    ${item.spaceAbove === true ? html`<div style="height: 5px;width: 100%;border-top: 1px solid var(--theme-border-color); margin-top: 5px;"></div>` : ''}
                                                    <div class="backpack-sidebar__backpack-page-link ${(organizationInfo.organization ? item.teamsLabel : item.label) === this.pageName ?'selected' : ''} ${item.clickable !== false ? 'clickable' : ''}" @click=${() => this.onSelectNavItem(item)}>
                                                        <div class="backpack-sidebar__backpack-page-name">
                                                            <span style="display:flex;align-items:center">
                                                                ${item.label === 'Personal Backpack' ? html`
                                                                        <account-avatar class="backpack-sidebar__img"
                                                                            .imageUrl=${accountInfo.account?.image_url}
                                                                            size="xs">
                                                                            ${getInitials(
                                                                                accountInfo.fullName,
                                                                                accountInfo.email
                                                                            )}
                                                                        </account-avatar>`
                                                                    : item.label === 'Team Backpack' ? html`
                                                                        <account-avatar class="backpack-sidebar__img" backgroundColor=${org_background_color} textColor=${org_text_color} ?isTeam=${isDefined(organizationInfo.organization)} size="xs">
                                                                            ${organizationInfo.organization?.name?.charAt(0) ?? accountInfo.fullName.charAt(0)}
                                                                        </account-avatar>`
                                                                    :html`<img class="backpack-sidebar__img" src=${ifDefined(item.img)} />
                                                                    `}
                                                                <span>${organizationInfo.organization ? item.teamsLabel : item.label}</span>
                                                            </span>
                                                            ${isDefined(item.subItems) || item.label === 'Folders' || item.label === 'Public Profiles' ? html`
                                                                <div style="display:flex;align-items:center;cursor:pointer">
                                                                    ${item.label === 'Folders' ? html`
                                                                        <mwc-icon
                                                                            class="backpack-sidebar__icon desktopOnly"
                                                                            @click=${this.triggerAddFolder}
                                                                        >
                                                                        add
                                                                        </mwc-icon>
                                                                        ${this.folders.length > 0 ? html `
                                                                            <mwc-icon @click=${(e: Event) => this.onExpandSubItems(e, item)} class="backpack-sidebar__icon">${item.isExpanded === true ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</mwc-icon>
                                                                            ` : ''}
                                                                    ` : ''}
                                                                    ${item.label === 'Public Profiles' ? html`
                                                                        <mwc-icon
                                                                            class="backpack-sidebar__icon desktopOnly"
                                                                            @click=${() => {
                                                                                this.addCreatorProfileModalComponent.open = true;
                                                                            }}
                                                                        >
                                                                        add
                                                                        </mwc-icon>
                                                                        ${this.creatorProfiles.length > 0 ? html `
                                                                            <mwc-icon @click=${(e: Event) => this.onExpandSubItems(e, item)} class="backpack-sidebar__icon">${item.isExpanded === true ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</mwc-icon>
                                                                        ` : ''}
                                                                    ` : ''}

                                                                </div>
                                                            ` : ''}
                                                        </div>
                                                    </div>
                                                    ${item.isExpanded === true && isDefined(item.subItems) ? html`
                                                        <div style="display:flex;flex-direction:column;width:100%">
                                                            ${item.subItems.map(subItem => {
                                                                if ((subItem.teammateOnly === true && organizationInfo.isGuest) || (subItem.label === 'Personal' && !this.showPersonalTopics)) {
                                                                    return '';
                                                                } else {
                                                                    return html`
                                                                        <div @click=${() => this.onSelectNavItem(subItem)} class="backpack-sidebar__backpack-page-link clickable ${this.filterType === subItem.filterType ? 'selected' : ''}">
                                                                            <div class="backpack-sidebar__backpack-page-name">
                                                                                <span style="margin-left:40px;flex:1; ${subItem.spaceAbove === true ? 'margin-top: 10px' : ''}">
                                                                                    ${organizationInfo.organization ? subItem.teamsLabel : subItem.label}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    `;
                                                                }
                                                            })}
                                                        </div>
                                                    ` : ''}
                                                    ${item.isExpanded === true && item.label === 'Folders' ? html`
                                                        <folder-selector
                                                            @on-update-folders=${this.onUpdateFolders}
                                                            @on-folder-select=${this.selectFolderFromSidebar}
                                                            ?isModal=${this.isModal}
                                                            .selectedFolderId=${this.selectedFolderId}
                                                            .folders=${this.folders}
                                                        >
                                                        </folder-selector>
                                                    ` : ''}
                                                    ${item.isExpanded === true && item.label === 'Public Profiles' ? html`
                                                        <div class="backpack-sidebar__profile-section">
                                                            ${this.creatorProfiles.map((creatorProfile) => {
                                                                return html`
                                                                    <div
                                                                        class="backpack-sidebar__backpack-page-link clickable ${
                                                                            this.selectedCreatorProfileId === creatorProfile._id.$oid
                                                                                ? 'selected'
                                                                                : ''
                                                                        }"
                                                                        @click=${() => this.onSelectCreatorProfile(creatorProfile._id.$oid)}
                                                                    >
                                                                        <span class="backpack-sidebar__backpack-page-name" style="margin-left:40px">
                                                                            @${creatorProfile.username}
                                                                        </span>
                                                                    </div>
                                                                `;
                                                            })}
                                                        </div>
                                                    ` : ''}
                                                `

                                    )}
                                </div>
                            `}
                      `
                    : ''}
                ${this.mobileMinified || detectMobileViewport() ? '' : html`
                    <blog-feed-component
                        layout="sidebar"
                        rss-url="https://www.proxi.co/blog/rss.xml"
                        .maxPosts=${10}>
                    </blog-feed-component>
                `}
                ${organizationInfo.organization && !organizationInfo.isGuest && !this.mobileMinified ? html`
                    ${BACKPACK_SIDEBAR_NAV_ITEMS_SETTINGS.map(
                        (item) =>
                            html`
                            ${item.spaceAbove === true ? html`<div style="height: 5px;width: 100%;border-top: 1px solid var(--theme-border-color); margin-top: 5px;"></div>` : ''}
                                <div class="backpack-sidebar__backpack-page-link clickable ${item.label === this.pageName ? 'selected' : ''}" @click=${() => this.onSelectNavItem(item)}>
                                    <div class="backpack-sidebar__backpack-page-name">
                                        <span style="display:flex;align-items:center">
                                            <img class="backpack-sidebar__img" src=${ifDefined(item.img)} />
                                            ${item.teamsLabel}
                                        </span>
                                    </div>
                                </div>
                            `
                    )}
                    <div style="height: 5px;width: 100%;border-top: 1px solid var(--theme-border-color); margin-top: 5px;"></div>
                ` : ''}
                ${!featuresInfo.duplicateMapEnabled && !this.mobileMinified && !organizationInfo.isGuest ? html`
                    <div class="backpack-sidebar__wrapper_cta desktopOnly">
                        <form-button
                            type="submit"
                            variant="outline"
                            ?fullWidth=${true}
                            @click=${async () => await this.checkout_feature(FeatureType.DuplicateMap, BillingOption.Annual)}>
                            <img src="/images/tiers/starter_small.png" style="margin-right: 5px" />
                            <span>Try Proxi Pro</span>
                        </form-button>
                    </div>`
                : ''}
                ${this.mobileMinified ? '' : html`
                    <div class="helpSection">
                        <span class="">
                            <help-menu
                                id="helpmenu"
                                type="backpack">
                                help
                            </help-menu>
                        </span>
                        <span class="helpOption" @click="${this.helpComponent?.submitFeatureRequestClick}">
                            <mwc-icon>forum</mwc-icon>
                            Feedback
                        </span>
                        <span class="helpOption" @click="${this.helpComponent?.learnClick}">
                            <mwc-icon>description</mwc-icon>
                            Docs
                        </span>
                        <span class="helpOption" @click="${this.helpComponent?.blogClick}">
                            <mwc-icon>grade</mwc-icon>
                            What's New
                        </span>
                            <span class="helpOption" @click="${this.onOpenDiscover}">
                            <mwc-icon>lightbulb</mwc-icon>
                            Explore Maps
                        </span>
                    </div>
                `}
                <div class="user-area" @click=${this.onOpenAccountInfo}>
                    <account-avatar
                        .imageUrl=${accountInfo.account?.image_url}
                        size="sm">
                        ${getInitials(
                            accountInfo.fullName,
                            accountInfo.email
                        )}
                    </account-avatar>
                    <div>
                        <b>${undefinedIfEmpty(accountInfo.fullName) ?? accountInfo.email}</b>
                    </div>
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        </div>

            <mwc-dialog id="addCreatorProfileModal" class="dialog" hideActions>
                <modal-dialog
                    size="lg"
                    @close="${() => this.addCreatorProfileModalComponent?.close()}"
                >
                    <add-creator-profile-modal
                        id="addCreatorProfileModalContent"
                        slot="body"
                        @on-add-creator-profile="${this.onAddCreatorProfile}"
                        @close="${() => this.addCreatorProfileModalComponent?.close()}"
                    >
                    </add-creator-profile-modal>
                </modal-dialog>
            </mwc-dialog>

            <mwc-dialog id="addFolderModalComponent" class="dialog" hideActions>
                <modal-dialog size="lg" @close="${() => this.addFolderModalComponent?.close()}">
                    <add-edit-folder-modal
                        id="addFolderModalContent"
                        slot="body"
                        type="add-folder"
                        @add-folder="${async (e: any) =>
                            await this.addFolder({
                                ...e,
                                detail: { ...e.detail, folderId: this.selectedFolderId },
                            })}"
                        @close="${() => this.addFolderModalComponent?.close()}"
                    >
                    </add-edit-folder-modal>
                </modal-dialog>
            </mwc-dialog>

            <mwc-dialog id="teamAccessComponent" class="dialog" hideActions>
                <modal-dialog @close=${this.teamAccessClose}>
                    <manage-team-access-component
                        slot="body"
                        .isCreating=${true}
                        .organization_id=${getFromLocalStorage('organization_id') ?? undefined}
                        .organization="${organizationInfo.organization}"
                        @closed=${this.teamAccessClose}
                        @team-created=${this.onTeamCreated}
                    >
                    </manage-team-access-component>
                </modal-dialog>
            </mwc-dialog>
    `;
    }

    static get styles() {
        return css`

            folder-selector {
                width: 100%;
            }

            .helpSection {
                display: inline-flex;
                background-color: lightgray;
                width: 100%;
                flex-wrap: wrap;
                align-items: center;
                justify-content: center;
                align-content: center;
                border-radius: 5px;
            }

            .helpOption {
                display: flex;
                align-items: center;
                gap: 2px;
                padding: 5px;
                cursor: pointer;
                border-radius: 5px;
                background-color: var(--theme-background-color);
                color: var(--main-primary-color);
                --mdc-icon-size: 16px;
            }

            .backpack-sidebar__link {
                display: flex;
                align-items: center;
                text-decoration: none;
                justify-content: space-between;
                margin-top: 10px;
                font-weight: semi-bold;
                color: var(--main-primary-color);
            }

            .backpack-sidebar__creator-profile-link {
                color: var(--main-primary-color);
                text-decoration: none;
                cursor: pointer;
                display: flex;
                align-items: center;
                padding-bottom: 14px;
            }

            .backpack-sidebar__creator-profile-link img {
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 50%;
                margin-right: 10px
            }

            .backpack-sidebar__wrapper {
                display: flex;
                flex-direction: column;
                align-content: space-between;
                height: 100%;
                overflow: auto;
                width: 260px;
            }

            .backpack-sidebar__wrapper_main {
                flex: 1;
                padding: 15px 10px;
                overflow: auto;
                display: flex;
                flex-direction: column;
            }

            .backpack-sidebar__wrapper_main::-webkit-scrollbar {
                width: 5px;
            }

            .backpack-sidebar__wrapper_main::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .backpack-sidebar__wrapper_main::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .backpack-sidebar__wrapper_cta {
                padding: 10px 5px;
            }

            .backpack-sidebar__backpack-page-link {
                width: 100%;
                color: var(--main-primary-color);
                text-decoration: none;
                padding: 4px 0px;
                display: flex;
                flex-direction: row;
                align-content: space-between;
                align-items: center;
                gap: 8px;
                box-sizing: border-box;
            }

            .backpack-sidebar__backpack-page-link.selected {
                font-weight: bolder;
            }

            .backpack-sidebar__backpack-page-link:hover {
                background: var(--theme-border-color);
                border-radius: 5px;
            }

            .backpac-sidebar__photo {
                max-height: 2.5rem;
                max-width: 2.5rem;
                border-radius: 50%;
                margin-right: 0.5rem;
            }

            .backpack-sidebar__backpack-links {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                padding: 4px 8px;
                flex: 1;
                overflow-y: auto;
            }

            .backpack-sidebar__backpack-page-name {
                display: flex;
                justify-content: space-between;
                flex: 1 1 0%;
            }

            .accountinfoholder {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 10px;
                margin-bottom: 0.5rem;
            }

            .backpack-sidebar__backpack-page-name > span {
                color: #49454F;
                font-size: 12px;
                line-height: 100%;
            }

            .backpack-sidebar__creator-profile-link.selected {
                color: var(--main-secondary-color);
            }

            .backpack-sidebar__title {
                display: flex;
                align-items: center;
                justify-content: space-between;
                font-weight: semi-bold;
                margin-top: 5px;
                min-height: 48px;
            }

            .backpack-sidebar__download {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-top: 10px;
                font-size: 12px;
                text-decoration: underline;
                cursor: pointer;
            }

            .backpack-sidebar__backpack-page-number {
                display: flex;
                padding: 2px 8px;
                align-items: center;
                border-radius: 16px;
                background: #F7F7F7;
                color: #000000;
                font-size: 12px;
                font-weight: 600;
            }

            .backpack-sidebar__backpack-page-number > span {
                color: var(--theme-input-color);
                text-align: center;
                font-size: 12px;
                font-weight: 700;
                line-height: 18px;
            }

            .backpack-sidebar__profile-section {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                overflow: auto;
            }


            .backpack-sidebar__button {
                margin-right: 0.25rem;
                --mdc-theme-secondary: var(--main-primary-color);
            }

            .backpack-sidebar__name {
                display: flex;
                align-items: center;
                flex: 1;
                padding: 0.25rem 0;
            }

            .hidden {
                display: none;
            }

            .backpack-sidebar__icon {
                --mdc-icon-size: 16px;
                margin-right: 7px;
                margin-left: 2px;
                cursor: pointer;
                vertical-align: middle;
                --mdc-icon-button-size: 2rem;
            }

            .backpack-sidebar__img {
                max-width: 16px;
                height: 16px;
                margin-left: 4px;
                margin-right: 12px;
            }

            .loadingImage {
                height: 15px;
            }

            .switchMenuContainer {
                border: 1px solid var(--theme-input-border-color);
                border-radius: 0.5rem;
                margin-bottom: 1rem;
                display: flex;
                box-shadow: 0 1px 2px 0 rgba(0,0,40,.1),0 2px 4px 0 rgba(0,0,40,.2),inset 0 -1px 0 0 var(--main-primary-color);
            }

            .name {
                margin-left: 3rem;
            }

            .user-area {
                min-height: 40px;
                display: flex;
                align-items: center;
                flex-direction: row;
                border-top: 1px solid var(--theme-border-color);
                margin-top: 1rem;
                gap: 10px;
                justify-content: center;
                cursor: pointer;
            }

            .user-area:hover {
                background: var(--theme-border-color);
                border-radius: 4px;
            }

            .clickable {
                cursor: pointer;
            }

            team-switch-menu {
                width: 100%;
            }

            @media (min-width: 551px) {
                .desktopOnly {
                    display: block;
                }

                .backpack-sidebar__link.desktopOnly {
                    display: flex;
                }

                .mobileOnly {
                    display: none;
                }
            }

            @media (max-width: 550px) {
                .backpack-sidebar__wrapper {
                    width: unset;
                }

                .user-area {
                    margin-top: 0;
                }

                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: block;
                }

                .accountinfoholder {
                    display: none;
                }

            }
        `;
    }
}
